import type { JobberFullProfileDetails } from '../../agency-data/AgencyInterfaces';

enum AttributeAnswerType {
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  RATING = 'RATING',
  REPEATING_FREE_TEXT = 'REPEATING_FREE_TEXT',
}

enum AttributeStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

enum AttributeSelectOptionStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

enum AttributeAssignmentSource {
  ADMIN = 'ADMIN',
  WORK_TASK_SUBMIT = 'WORK_TASK_SUBMIT',
}

enum AttributeRequirementSeverity {
  REQUIRED = 'REQUIRED',
  PREFERRED = 'PREFERRED',
  OPTIONAL = 'OPTIONAL',
}

interface AttributeSelectOptionDTO {
  readonly optionId: string;
  readonly optionLabel: string;
  readonly sortOrder?: number | null;
}

interface AgencyAttributeDTO {
  readonly attributeId: string;
  readonly displayName: string;
  readonly description?: string | null;
  readonly answerType: AttributeAnswerType;
  readonly status: AttributeStatus;
  readonly selectOptions: readonly AttributeSelectOptionDTO[];
  readonly ratingMin?: number | null;
  readonly ratingMax?: number | null;
}

interface AgencyAttributeListRespDTO {
  readonly attributes: readonly AgencyAttributeDTO[];
}

interface CreateAttributeSelectOptionReqDTO {
  readonly optionLabel: string;
  readonly sortOrder?: number | null;
}

interface CreateAgencyAttributeReqDTO {
  readonly displayName: string;
  readonly description?: string | null;
  readonly answerType: AttributeAnswerType;
  readonly selectOptions?: readonly CreateAttributeSelectOptionReqDTO[];
  readonly ratingMin?: number | null;
  readonly ratingMax?: number | null;
}

interface RenameAgencyAttributeReqDTO {
  readonly attributeId: string;
  readonly displayName: string;
  readonly description?: string | null;
}

interface ArchiveAgencyAttributeReqDTO {
  readonly attributeId: string;
}

interface AddAgencyAttributeSelectOptionReqDTO {
  readonly attributeId: string;
  readonly optionLabel: string;
  readonly sortOrder?: number | null;
}

interface RenameAgencyAttributeSelectOptionReqDTO {
  readonly attributeId: string;
  readonly optionId: string;
  readonly optionLabel: string;
}

interface DisableAgencyAttributeSelectOptionReqDTO {
  readonly attributeId: string;
  readonly optionId: string;
}

interface AttributeGroupMemberDTO {
  readonly attributeId: string;
  readonly acceptedValues: readonly string[];
}

interface AttributeGroupDTO {
  readonly groupId: string;
  readonly displayName: string;
  readonly description?: string | null;
  readonly status: AttributeStatus;
  readonly memberAttributeIds: readonly string[];
  readonly members: readonly AttributeGroupMemberDTO[];
}

interface AttributeGroupListRespDTO {
  readonly groups: readonly AttributeGroupDTO[];
}

interface CreateAttributeGroupReqDTO {
  readonly displayName: string;
  readonly description?: string | null;
  readonly members: readonly AttributeGroupMemberDTO[];
}

interface RenameAttributeGroupReqDTO {
  readonly groupId: string;
  readonly displayName: string;
  readonly description?: string | null;
}

interface ArchiveAttributeGroupReqDTO {
  readonly groupId: string;
}

interface UpdateAttributeGroupMembersReqDTO {
  readonly groupId: string;
  readonly members: readonly AttributeGroupMemberDTO[];
}

interface WorkerAttributeAssignmentDTO {
  readonly primaryWorkyUserId: string;
  readonly attributeId: string;
  readonly answerType: AttributeAnswerType;
  readonly selectOptionId?: string | null;
  readonly selectOptionIds?: readonly string[] | null;
  readonly ratingValue?: number | null;
  readonly source: AttributeAssignmentSource;
}

interface WorkerAttributeAssignmentListRespDTO {
  readonly assignments: readonly WorkerAttributeAssignmentDTO[];
}

interface UpsertWorkerAttributeAssignmentReqDTO {
  readonly primaryWorkyUserId: string;
  readonly attributeId: string;
  readonly selectOptionId?: string | null;
  readonly selectOptionIds?: readonly string[] | null;
  readonly ratingValue?: number | null;
}

interface WorkerAttributeAnswerValueReqDTO {
  readonly attributeId: string;
  readonly selectOptionId?: string | null;
  readonly selectOptionIds?: readonly string[] | null;
  readonly ratingValue?: number | null;
}

interface ApplyAttributeGroupToWorkerReqDTO {
  readonly primaryWorkyUserId: string;
  readonly groupId: string;
  readonly explicitAnswers: readonly WorkerAttributeAnswerValueReqDTO[];
}

interface OrgAttributeRequirementDTO {
  readonly attributeId: string;
  readonly severity: AttributeRequirementSeverity;
  readonly allowedOptionIds: readonly string[];
  readonly minRating?: number | null;
  readonly maxRating?: number | null;
}

interface OrgAttributeRequirementListRespDTO {
  readonly orgName: string;
  readonly requirements: readonly OrgAttributeRequirementDTO[];
}

interface UpsertOrgAttributeRequirementReqDTO {
  readonly orgName: string;
  readonly attributeId: string;
  readonly severity: AttributeRequirementSeverity;
  readonly allowedOptionIds: readonly string[];
  readonly minRating?: number | null;
  readonly maxRating?: number | null;
}

interface RemoveOrgAttributeRequirementReqDTO {
  readonly orgName: string;
  readonly attributeId: string;
}

interface RemoveWorkerAttributeAssignmentReqDTO {
  readonly primaryWorkyUserId: string;
  readonly attributeId: string;
}

interface WorkerAttributeSearchFilterReqDTO {
  readonly attributeId: string;
  readonly selectedOptionIds?: readonly string[];
  readonly ratingMin?: number | null;
  readonly ratingMax?: number | null;
}

interface SearchWorkersByAttributesReqDTO {
  readonly attributeFilters: readonly WorkerAttributeSearchFilterReqDTO[];
}

interface WorkerAttributeSearchResultDTO {
  readonly workforceWorker: JobberFullProfileDetails;
  readonly matchingAssignments: readonly WorkerAttributeAssignmentDTO[];
}

interface WorkerAttributeSearchListRespDTO {
  readonly workers: readonly WorkerAttributeSearchResultDTO[];
}

interface CareProfileAttributeChoiceDTO {
  readonly optionId: string;
  readonly optionLabel: string;
  readonly status: AttributeSelectOptionStatus;
}

interface CareProfileAttributeAssignmentDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
  readonly displayName: string;
  readonly answerType: AttributeAnswerType;
  readonly catalogStatus: AttributeStatus;
  readonly choices: readonly CareProfileAttributeChoiceDTO[];
  readonly selectedOptionIds: readonly string[];
  readonly repeatingFreeTextLines: readonly string[];
  readonly description?: string | null;
  readonly selectedOptionId?: string | null;
}

interface CareProfileAttributeAssignmentListRespDTO {
  readonly assignments: readonly CareProfileAttributeAssignmentDTO[];
}

interface AddCareProfileAttributeAssignmentReqDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
}

interface AddCareProfileAttributeAssignmentWithAnswerReqDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
  readonly selectedOptionIds?: readonly string[];
  readonly repeatingFreeTextLines?: readonly string[];
  readonly selectedOptionId?: string | null;
}

interface ReplaceCareProfileAttributeAnswerReqDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
  readonly selectedOptionIds?: readonly string[];
  readonly repeatingFreeTextLines?: readonly string[];
  readonly selectedOptionId?: string | null;
}

interface RemoveCareProfileAttributeAssignmentReqDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
}

interface CareProfileAttributeAssignmentRemovedRespDTO {
  readonly careProfilePrimaryWorkyUserId: string;
  readonly attributeId: string;
}

export {
  AttributeAnswerType,
  AttributeStatus,
  AttributeSelectOptionStatus,
  AttributeAssignmentSource,
  AttributeRequirementSeverity,
  AttributeSelectOptionDTO,
  AgencyAttributeDTO,
  AgencyAttributeListRespDTO,
  CreateAttributeSelectOptionReqDTO,
  CreateAgencyAttributeReqDTO,
  RenameAgencyAttributeReqDTO,
  RenameAgencyAttributeSelectOptionReqDTO,
  DisableAgencyAttributeSelectOptionReqDTO,
  ArchiveAgencyAttributeReqDTO,
  AddAgencyAttributeSelectOptionReqDTO,
  AttributeGroupDTO,
  AttributeGroupListRespDTO,
  AttributeGroupMemberDTO,
  CreateAttributeGroupReqDTO,
  RenameAttributeGroupReqDTO,
  ArchiveAttributeGroupReqDTO,
  UpdateAttributeGroupMembersReqDTO,
  WorkerAttributeAssignmentDTO,
  WorkerAttributeAssignmentListRespDTO,
  UpsertWorkerAttributeAssignmentReqDTO,
  WorkerAttributeAnswerValueReqDTO,
  ApplyAttributeGroupToWorkerReqDTO,
  OrgAttributeRequirementDTO,
  OrgAttributeRequirementListRespDTO,
  UpsertOrgAttributeRequirementReqDTO,
  RemoveOrgAttributeRequirementReqDTO,
  RemoveWorkerAttributeAssignmentReqDTO,
  WorkerAttributeSearchFilterReqDTO,
  SearchWorkersByAttributesReqDTO,
  WorkerAttributeSearchResultDTO,
  WorkerAttributeSearchListRespDTO,
  CareProfileAttributeChoiceDTO,
  CareProfileAttributeAssignmentDTO,
  CareProfileAttributeAssignmentListRespDTO,
  AddCareProfileAttributeAssignmentReqDTO,
  AddCareProfileAttributeAssignmentWithAnswerReqDTO,
  ReplaceCareProfileAttributeAnswerReqDTO,
  RemoveCareProfileAttributeAssignmentReqDTO,
  CareProfileAttributeAssignmentRemovedRespDTO,
};
